import React, { useEffect, useRef, useCallback } from 'react';

interface AutoSizingTextareaProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  onBlur?: () => void;
  onBackspace?: () => void;
  readOnly?: boolean;
}

const AutoSizingTextarea: React.FC<AutoSizingTextareaProps> = ({
  value,
  onChange,
  className = '',
  placeholder,
  autoFocus,
  onBlur,
  onBackspace,
  readOnly
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);

  const updateSize = useCallback(() => {
    if (spanRef.current && textareaRef.current) {
      spanRef.current.textContent = value + '\n';
      const scrollHeight = spanRef.current.scrollHeight;
      textareaRef.current.style.height = `${scrollHeight}px`;
      textareaRef.current.scrollTop = 0;
    }
  }, [value]);

  useEffect(() => {
    updateSize();
    const resizeObserver = new ResizeObserver(updateSize);
    if (textareaRef.current) {
      resizeObserver.observe(textareaRef.current);
      if (autoFocus) {
        textareaRef.current.focus();
      }
    }
    return () => resizeObserver.disconnect();
  }, [updateSize, autoFocus]);

  useEffect(() => {
    updateSize();
  }, [value, updateSize]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      const handleScroll = () => {
        textarea.scrollTop = 0;
      };
      textarea.addEventListener('scroll', handleScroll);
      return () => textarea.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    updateSize();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Backspace' && value === '' && onBackspace) {
      event.preventDefault(); // Prevent the default backspace action
      onBackspace();
    }
  };

  const handleBlur = () => {
    if (value === '' && onBlur) {
      onBlur();
    }
  };

  return (
    <div className="relative-parent">
      <span ref={spanRef} className="size-calibration" aria-hidden="true"></span>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onInput={updateSize}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={`autosized-input ${className}`}
        rows={1}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{ overflowY: 'hidden', resize: 'none' }}
      />
    </div>
  );
};

export default AutoSizingTextarea; 